import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { HttpClientError } from "effect/http";
import { RpcClientError } from "effect/rpc/RpcClientError";
import { Cause } from "effect";
import { frontdeskDiagnosticLogger } from "./diagnostic-logger";
import type {
  BridgeDiagnosticReportResult,
  HandledErrorDiagnosticContext,
  HandledErrorDiagnosticEvent,
  PlatformAdapter,
} from "../platform/types";

type ToastErrorMessage = Parameters<typeof toast.error>[0];
type ToastErrorOptions = Parameters<typeof toast.error>[1];
type ToastWarningMessage = Parameters<typeof toast.warning>[0];
type ToastWarningOptions = Parameters<typeof toast.warning>[1];

let activePlatform: PlatformAdapter | undefined;
let installationCount = 0;
let originalToastError: typeof toast.error | undefined;
let originalToastWarning: typeof toast.warning | undefined;
let lastStructuredReportAt = 0;

const TOAST_FALLBACK_DELAY_MS = 1500;
const REPORT_DEDUPLICATION_WINDOW_MS = 5 * 60 * 1000;
const MAX_RECENT_REPORTS = 200;
const reportedErrorObjects = new WeakSet<Error>();
const recentReportTimes = new Map<string, number>();
const SAFE_ERROR_NAMES = new Set([
  "Error",
  "TypeError",
  "SyntaxError",
  "RangeError",
  "ReferenceError",
  "AbortError",
  "TimeoutError",
  "TimeoutException",
  "FrontdeskRequestTimeoutError",
  "RequestError",
  "ResponseError",
  "HttpClientError",
  "TransportError",
  "StatusCodeError",
  "DecodeError",
  "EncodeError",
  "InvalidUrlError",
  "EmptyBodyError",
  "RpcClientError",
  "UnknownError",
]);

/**
 * Creates a short diagnostic ID suitable for user-facing support messages.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const diagnosticId = createHandledErrorDiagnosticId();
 * ```
 */
export function createHandledErrorDiagnosticId(): string {
  return uuidv4().replace(/-/g, "").slice(0, 8).toUpperCase();
}

function currentRoute(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const hashRoute = window.location.hash.split("?")[0];
  return `${window.location.pathname}${hashRoute}`;
}

function describeError(error: unknown): { name: string } {
  if (error instanceof Error) {
    return {
      name: SAFE_ERROR_NAMES.has(error.name) ? error.name : "Error",
    };
  }

  return { name: "UnknownError" };
}

type ErrorFacts = Pick<
  HandledErrorDiagnosticEvent,
  "errorCategory" | "httpStatus"
>;

// The reporting ingress unwraps library-owned errors; only fixed categories leave it.
function classifyError(error: unknown, depth = 0): ErrorFacts {
  if (depth >= 8) return { errorCategory: "unknown" };

  if (error instanceof RpcClientError) {
    if (error.reason._tag === "HttpError") return classifyError(error.reason, depth + 1);
    const nested = "cause" in error.reason
      ? classifyError(error.reason.cause, depth + 1)
      : { errorCategory: "unknown" } satisfies ErrorFacts;
    return nested.errorCategory === "unknown" || nested.errorCategory === "runtime"
      ? { errorCategory: "protocol" }
      : nested;
  }
  if (HttpClientError.isHttpClientError(error)) {
    return classifyError(error.reason, depth + 1);
  }
  if (error instanceof HttpClientError.HttpClientErrorSchema) {
    const nested = classifyError(error.cause, depth + 1);
    if (nested.errorCategory !== "unknown" && nested.errorCategory !== "runtime") return nested;
    switch (error.kind) {
      case "TransportError": return { errorCategory: "transport" };
      case "StatusCodeError": return { errorCategory: "http-status" };
      case "DecodeError":
      case "EmptyBodyError": return { errorCategory: "decode" };
      default: return { errorCategory: "protocol" };
    }
  }
  if (error instanceof HttpClientError.StatusCodeError) {
    return { errorCategory: "http-status", httpStatus: error.response.status };
  }
  if (error instanceof HttpClientError.DecodeError || error instanceof HttpClientError.EmptyBodyError) {
    const nested = classifyError(error.cause, depth + 1);
    return nested.errorCategory === "timeout" || nested.errorCategory === "cancelled"
      ? nested
      : { errorCategory: "decode" };
  }
  if (error instanceof HttpClientError.TransportError) {
    const nested = classifyError(error.cause, depth + 1);
    return nested.errorCategory === "timeout" || nested.errorCategory === "cancelled"
      ? nested
      : { errorCategory: "transport" };
  }
  if (error instanceof HttpClientError.EncodeError || error instanceof HttpClientError.InvalidUrlError) {
    return { errorCategory: "protocol" };
  }
  if (Cause.isTimeoutError(error)) return { errorCategory: "timeout" };
  if (error instanceof Error) {
    switch (error.name) {
      case "FrontdeskRequestTimeoutError":
      case "TimeoutError":
      case "TimeoutException":
        return { errorCategory: "timeout" };
      case "AbortError":
        return { errorCategory: "cancelled" };
      case "SyntaxError":
        return { errorCategory: "decode" };
      default:
        return { errorCategory: "runtime" };
    }
  }
  return { errorCategory: "unknown" };
}

function toastContext(
  options: ToastErrorOptions | ToastWarningOptions,
): HandledErrorDiagnosticContext | undefined {
  const context: { [key: string]: string | number | boolean | undefined } = {};

  if (options?.id !== undefined) {
    context.toastId = String(options.id);
  }

  if (options?.toasterId !== undefined) {
    context.toasterId = options.toasterId;
  }

  return Object.keys(context).length > 0 ? context : undefined;
}

function reportToastTelemetry(
  severity: "error" | "warning",
  _message: ToastErrorMessage | ToastWarningMessage,
  options: ToastErrorOptions | ToastWarningOptions,
): void {
  if (Date.now() - lastStructuredReportAt < TOAST_FALLBACK_DELAY_MS) {
    return;
  }

  void reportOperationalError({
    action: `toast.${severity}`,
    severity,
    source: "toast",
    context: toastContext(options),
  });
}

function cleanupRecentReports(now: number): void {
  if (recentReportTimes.size <= MAX_RECENT_REPORTS) {
    return;
  }

  for (const [key, reportedAt] of recentReportTimes) {
    if (now - reportedAt >= REPORT_DEDUPLICATION_WINDOW_MS) {
      recentReportTimes.delete(key);
    }
  }

  while (recentReportTimes.size > MAX_RECENT_REPORTS) {
    const oldestKey = recentReportTimes.keys().next().value;
    if (oldestKey === undefined) {
      return;
    }
    recentReportTimes.delete(oldestKey);
  }
}

function shouldSuppressReport({
  action,
  severity,
  source,
  error,
  errorName,
  errorCategory,
  httpStatus,
}: {
  readonly action: string;
  readonly severity: HandledErrorDiagnosticEvent["severity"];
  readonly source: HandledErrorDiagnosticEvent["source"];
  readonly error?: unknown;
  readonly errorName?: string;
  readonly errorCategory?: HandledErrorDiagnosticEvent["errorCategory"];
  readonly httpStatus?: number;
}): boolean {
  if (error instanceof Error && reportedErrorObjects.has(error)) {
    return true;
  }

  const now = Date.now();
  cleanupRecentReports(now);
  const reportKey = [
    action,
    severity,
    source,
    errorName ?? (error instanceof Error ? error.name : "UnknownError"),
    httpStatus ?? "no-status",
    errorCategory ?? "unknown",
  ].join("|");
  const lastReportedAt = recentReportTimes.get(reportKey);
  if (
    lastReportedAt !== undefined &&
    now - lastReportedAt < REPORT_DEDUPLICATION_WINDOW_MS
  ) {
    return true;
  }

  recentReportTimes.set(reportKey, now);
  if (error instanceof Error) {
    reportedErrorObjects.add(error);
  }
  if (source !== "toast") {
    lastStructuredReportAt = now;
  }
  return false;
}

/**
 * Creates an error with safe text while retaining the original stack frames.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const safeError = createTelemetrySafeError("patient.search", error);
 * ```
 */
export function createTelemetrySafeError(
  action: string,
  error: unknown,
): Error | undefined {
  if (error === undefined) {
    return undefined;
  }

  const safeError = new Error(`${action} failed`);
  if (!(error instanceof Error)) {
    safeError.name = "UnknownError";
    return safeError;
  }

  safeError.name = SAFE_ERROR_NAMES.has(error.name) ? error.name : "Error";
  if (error.stack) {
    const originalFrames = error.stack.split("\n").slice(1);
    safeError.stack = [
      `${safeError.name}: ${safeError.message}`,
      ...originalFrames,
    ].join("\n");
  }
  return safeError;
}

/**
 * Reports an operational failure once with PII-safe facts and its original stack.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * await reportOperationalError({ action: "patient.search", severity: "error", source: "rpc", error });
 * ```
 */
export async function reportOperationalError({
  platform = activePlatform,
  action,
  diagnosticId = createHandledErrorDiagnosticId(),
  severity,
  source,
  transport,
  route = currentRoute(),
  component,
  userMessage: _userMessage,
  error,
  interrupted = false,
  httpStatus,
  durationMs,
  context,
}: {
  readonly platform?: PlatformAdapter;
  readonly action: string;
  readonly diagnosticId?: string;
  readonly severity: HandledErrorDiagnosticEvent["severity"];
  readonly source: HandledErrorDiagnosticEvent["source"];
  readonly transport?: string;
  readonly route?: string;
  readonly component?: string;
  readonly userMessage?: string;
  readonly error?: unknown;
  /** Native runner observed an interruption-only Exit before squashing its error. */
  readonly interrupted?: boolean;
  readonly httpStatus?: number;
  readonly durationMs?: number;
  readonly context?: HandledErrorDiagnosticContext;
}): Promise<BridgeDiagnosticReportResult | undefined> {
  const errorDetails = error === undefined ? undefined : describeError(error);
  const errorFacts: ErrorFacts = interrupted
    ? { errorCategory: "cancelled" }
    : classifyError(error);
  const status = httpStatus ?? errorFacts.httpStatus;
  if (
    shouldSuppressReport({
      action,
      severity,
      source,
      error,
      errorName: errorDetails?.name,
      errorCategory: errorFacts.errorCategory,
      httpStatus: status,
    })
  ) {
    return undefined;
  }

  const originalError = createTelemetrySafeError(action, error);
  if (!platform?.reportHandledError) {
    if (severity === "error" && originalError) {
      globalThis.reportError?.(originalError);
    }
    return undefined;
  }

  try {
    const result = await platform.reportHandledError({
      action,
      diagnosticId,
      severity,
      source,
      transport,
      route,
      component,
      errorName: errorDetails?.name,
      errorMessage: originalError?.message,
      originalError,
      errorCategory: errorFacts.errorCategory,
      httpStatus: status,
      durationMs,
      context,
    });
    return result === undefined ? undefined : result;
  } catch (reportError) {
    frontdeskDiagnosticLogger.warn(
      "Operational telemetry report failed",
      reportError instanceof Error ? reportError : String(reportError),
    );
    return undefined;
  }
}

/**
 * Reports an error that was caught and converted into user-visible UI.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * await reportHandledError({ platform, action: "settings.load", severity: "error", source: "explicit" });
 * ```
 */
export function reportHandledError({
  platform,
  action,
  diagnosticId = createHandledErrorDiagnosticId(),
  severity,
  source,
  route = currentRoute(),
  component,
  userMessage,
  error,
  httpStatus,
  context,
}: {
  readonly platform: PlatformAdapter;
  readonly action: string;
  readonly diagnosticId?: string;
  readonly severity: HandledErrorDiagnosticEvent["severity"];
  readonly source: HandledErrorDiagnosticEvent["source"];
  readonly route?: string;
  readonly component?: string;
  readonly userMessage?: string;
  readonly error?: unknown;
  readonly httpStatus?: number;
  readonly context?: HandledErrorDiagnosticContext;
}): Promise<BridgeDiagnosticReportResult | undefined> {
  return reportOperationalError({
    platform,
    action,
    diagnosticId,
    severity,
    source,
    route,
    component,
    userMessage,
    error,
    httpStatus,
    context,
  });
}

/**
 * Installs telemetry for handled error toasts.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const uninstall = installHandledErrorToastTelemetry(platform);
 * uninstall();
 * ```
 */
export function installHandledErrorToastTelemetry(
  platform: PlatformAdapter,
): () => void {
  activePlatform = platform;
  installationCount += 1;

  if (!originalToastError) {
    originalToastError = toast.error;
    const patchedError: typeof toast.error = (message, options) => {
      reportToastTelemetry("error", message, options);
      return originalToastError?.(message, options) ?? "";
    };
    toast.error = patchedError;
  }

  if (!originalToastWarning) {
    originalToastWarning = toast.warning;
    const patchedWarning: typeof toast.warning = (message, options) => {
      reportToastTelemetry("warning", message, options);
      return originalToastWarning?.(message, options) ?? "";
    };
    toast.warning = patchedWarning;
  }

  return () => {
    installationCount -= 1;
    if (installationCount > 0) {
      return;
    }

    if (originalToastError) {
      toast.error = originalToastError;
      originalToastError = undefined;
    }

    if (originalToastWarning) {
      toast.warning = originalToastWarning;
      originalToastWarning = undefined;
    }

    activePlatform = undefined;
    installationCount = 0;
  };
}
